import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, createSafeQuery, fetchApi, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { queryKeys, sharedApi } from "@cartero/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": lets users link/unlink Google and Apple from the
 * profile. Shows only providers ENABLED in the backend. Respects the
 * last-method safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Reads ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23 / CC-60 · phase 2 · the BRIDGE. This `queryKey` is SHARED with
  // `components/auth/social-auth-buttons.tsx`, which mounts first (login and
  // registration). While either one swallowed the failure, the other read a
  // FABRICATED `success` from cache and did not request again: therefore both
  // `queryFn`s must throw. See the long note in that file.
  const providersQuery = createSafeQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => fetchApi(sharedApi.socialProviders(api)),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }));

  // P23 / CC-60 · phase 2 · I3, the part the mechanical fix did NOT close.
  //
  // `sharedApi.loginMethods` uses `openapi-fetch`: it NEVER throws; it returns
  // `{data, error}`. With `return data ?? {providers: [], has_password: false}`,
  // a 500 resolved as **SUCCESS with the default object**, so
  // `methodsQuery.isError` could NEVER become `true` and the
  // `<QueryErrorState testId="connected-accounts-error">` below was
  // DEAD CODE: visible in the diff, passing `tsc`, and never rendered.
  // Worse: the default object paints "Not connected" + "Connect" over a
  // Google account that IS linked — exactly the lie I3
  // targets. Measured in a browser (`p23-admin-error-reachability.spec.ts`),
  // not inferred from the code.
  //
  // The `isSuccess` guard in the `createMemo` does not help here, because the state WAS
  // `success`. The failure must propagate from the SOURCE: `fetchApi` throws
  // on any non-2xx (and on an empty body), which is what TanStack needs
  // to transition to `error`.
  const methodsQuery = createSafeQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => fetchApi(sharedApi.loginMethods(api)),
    retry: false,
  }));

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: `.data` only in `isSuccess`.
    const p = providersQuery.isSuccess ? providersQuery.data : undefined;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2. Without the guard, when `login-methods` failed `?? []` made
  // the screen say "Not connected" for a Google account that WAS connected
  // — and offer "Connect" for an already connected account.
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? (methodsQuery.data?.providers ?? []) : [],
  );
  const hasPassword = createMemo(() => methodsQuery.data?.has_password ?? false);

  /**
   * Did EITHER of the card's two reads fail?
   *
   * P23 / CC-60 · phase 2. Making the `queryFn`s throw is not enough by itself:
   * on error `enabledProviders()` is `[]`, and the `<Show>` wrapping the
   * card hid it ENTIRELY — the error box was still unreachable,
   * just for another reason. The card must also exist when it could not
   * be read, precisely when the user needs to know.
   */
  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const retryAll = () => {
    void providersQuery.refetch();
    void methodsQuery.refetch();
  };

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social provider).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback after returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23 / CC-60 · I3: error is WRAPPED (on error `isPending` is
              `false`, so chaining would leave error + skeleton simultaneously). */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              title={t(msg`Couldn't load your linked accounts`)}
              onRetry={retryAll}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
